import { TabOption, Tabs } from '@praetorian-chariot/ui';
import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

import { AppRouteBody } from '@/components/AppRouteBody';
import { CustomerTable } from '@/sections/customerManagement/components/CustomerTable';
import { GlobalActions } from '@/sections/customerManagement/components/GlobalActions';
import { AIAssistant } from '@/sections/insights/aiAssistant';
import QueryBuilder from '@/sections/insights/queryBuilder';
import { useAuth } from '@/state/auth';

const praetorianOnlyTabs = new Set(['global-ai-assistant', 'global-actions']);

const allCustomerTabs: TabOption[] = [
  { label: 'Global Dashboard', value: 'global-customer-dashboard' },
  { label: 'Global AI Assistant', value: 'global-ai-assistant' },
  { label: 'Global Query Builder', value: 'global-query-builder' },
  { label: 'Global Actions', value: 'global-actions' },
];

export const CustomerManagement: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isPraetorianUser } = useAuth();

  const customerTabs = isPraetorianUser
    ? allCustomerTabs
    : allCustomerTabs.filter(
        tab => !praetorianOnlyTabs.has(tab.value as string)
      );

  const VALID_TABS = customerTabs.map(tab => tab.value);

  const [selectedTabValue, setSelectedTabValue] = useState(() => {
    const urlTab = searchParams.get('tab');
    return urlTab && VALID_TABS.includes(urlTab)
      ? urlTab
      : 'global-customer-dashboard';
  });

  // Gate to prevent race condition during intentional navigation
  const isUpdatingTabRef = useRef(false);

  /**
   * Synchronizes tab selection with URL parameters to enable deep linking
   * and browser back/forward navigation.
   *
   * Tab selection is driven ONLY by the explicit ?tab=<value> parameter.
   * The QueryBuilder's ?state= parameter is managed independently and does
   * not influence tab selection to prevent race conditions.
   *
   * @remarks
   * Previous implementation had race condition where QueryBuilder's automatic
   * ?state= parameter updates would trigger this effect and cause tab switching.
   * Fixed by removing state inference logic and only responding to explicit tab param.
   *
   * Related: Linear ticket CHARIOT-1538
   */
  useEffect(() => {
    // Skip effect if we're in the middle of intentional navigation
    if (isUpdatingTabRef.current) return;

    const urlTab = searchParams.get('tab');

    // Only respond to explicit tab parameter with guard to prevent unnecessary updates
    if (urlTab && VALID_TABS.includes(urlTab) && urlTab !== selectedTabValue) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedTabValue(urlTab);
    }

    // 🚨 DEFENSIVE CHECK: Warn if state inference logic gets re-added
    if (import.meta.env.DEV) {
      const stateParam = searchParams.get('state');
      if (stateParam && !urlTab) {
        console.warn(
          '[CHARIOT-1538 REGRESSION WARNING] State param present without tab param.\n' +
            'This should NOT influence tab selection to prevent race conditions.\n' +
            'See: https://linear.app/praetorian/issue/CHARIOT-1538'
        );
      }
    }
  }, [searchParams, selectedTabValue]); // Matches Insights pattern with guard

  /**
   * TODO: Consider refactoring to URL-as-single-source-of-truth pattern
   * (see Insights.tsx lines 52-61 for reference implementation)
   *
   * Would eliminate:
   * - Ref-based gate pattern (isUpdatingTabRef)
   * - useEffect synchronization logic
   * - Dual state management (URL + local state)
   *
   * Benefits:
   * - Simpler mental model
   * - No timing dependencies
   * - Matches proven pattern from Insights.tsx
   *
   * Estimated effort: 1-2 hours + testing
   * Priority: Low (current implementation is production-safe after cleanup)
   *
   * Reference: .architecture-analysis/frontend-architecture-race-condition-review.md
   * Linear ticket: CHARIOT-1538
   */

  const setSelectedTab = (newTab: string) => {
    if (VALID_TABS.includes(newTab)) {
      // Set gate to prevent useEffect from interfering during navigation
      isUpdatingTabRef.current = true;

      setSelectedTabValue(newTab);
      setSearchParams(
        prev => {
          const newParams = new URLSearchParams(prev);
          newParams.set('tab', newTab);
          return newParams;
        },
        { replace: true }
      );

      // Release gate after next tick - matches Insights pattern
      setTimeout(() => {
        isUpdatingTabRef.current = false;
      }, 0);
    }
  };

  const selectedTab: TabOption | null =
    customerTabs.find(tab => tab.value === selectedTabValue) || null;

  const handleTabSelect = (tab: TabOption) => {
    const targetValue = tab.value as string;

    if (
      selectedTabValue === 'global-query-builder' &&
      targetValue !== 'global-query-builder'
    ) {
      setSelectedTab(targetValue);
    } else {
      setSelectedTab(targetValue);
    }
  };

  const renderContent = () => {
    switch (selectedTabValue) {
      case 'global-ai-assistant':
        return <AIAssistant />;
      case 'global-query-builder':
        return <QueryBuilder isGlobal={true} />;
      case 'global-customer-dashboard':
        return <CustomerTable />;
      case 'global-actions':
        return <GlobalActions />;
      default:
        return <CustomerTable />;
    }
  };

  return (
    <AppRouteBody
      title={isPraetorianUser ? 'Customer Management' : 'Tenant Management'}
    >
      <div className="flex h-full flex-col overflow-hidden">
        <p className="text-chariot-text-secondary mb-4 text-sm">
          Global engagement management across all tenants
        </p>
        <Tabs
          tabs={customerTabs}
          selected={selectedTab}
          onSelect={handleTabSelect}
          variant="primary"
        />
        <div className="mt-4 h-full overflow-y-auto pb-6">
          {renderContent()}
        </div>
      </div>
    </AppRouteBody>
  );
};
